import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getHotLives } from "@/utils/liveData";
import { getAnchorById } from "@/mocks/anchors";
import { formatViewers } from "@/utils/format";

export default function HeroCarousel() {
  const hotLives = getHotLives(5);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % hotLives.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused, hotLives.length]);

  const toPrev = () => setActive((prev) => (prev - 1 + hotLives.length) % hotLives.length);
  const toNext = () => setActive((prev) => (prev + 1) % hotLives.length);

  const current = hotLives[active];
  const anchor = getAnchorById(current.anchorId);

  return (
    <section
      className="relative w-full overflow-hidden rounded-lg bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[360px] w-full md:h-[460px]">
        {hotLives.map((live, index) => (
          <div
            key={live.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={live.cover}
              alt={`${live.title} - 蜜梨直播焦点直播`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/10" />

        <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-10">
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
              <span className="h-1.5 w-1.5 rounded-full bg-background-50" />
              正在直播
            </span>
            <span className="rounded-full bg-background-50/20 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
              焦点推荐
            </span>
          </div>
          <h2 className="mt-3 max-w-3xl font-heading text-2xl font-extrabold leading-tight text-background-50 md:text-4xl">
            {current.title}
          </h2>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 overflow-hidden rounded-full bg-secondary-200">
                <img
                  src={anchor?.avatar}
                  alt={`${anchor?.name} 头像`}
                  className="h-full w-full object-cover object-top"
                />
              </span>
              <span className="text-sm font-medium text-background-50">{anchor?.name}</span>
            </div>
            <span className="flex items-center gap-1 text-sm text-background-100">
              <i className="ri-eye-line text-base" />
              {formatViewers(current.viewers)} 人在看
            </span>
            <Link
              to={`/live/${current.id}`}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-base" />
              立即观看
            </Link>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一条"
          onClick={toPrev}
          className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:left-6"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一条"
          onClick={toNext}
          className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:right-6"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-5 right-5 flex items-center gap-2 md:bottom-10 md:right-10">
          {hotLives.map((live, index) => (
            <button
              key={live.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 条`}
              onClick={() => setActive(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active ? "w-6 bg-primary-500" : "w-3 bg-background-50/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}